
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Treatment } from '@/data/treatmentData';
import { Check, X } from 'lucide-react';

interface AddServiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableTreatments: {
    dental: Treatment[];
    cosmetic: Treatment[];
  };
  selectedTreatments: {
    dental: Treatment[];
    cosmetic: Treatment[];
  };
  onSave: (treatments: { dental: Treatment[]; cosmetic: Treatment[] }) => void;
}

const AddServiceModal: React.FC<AddServiceModalProps> = ({
  open,
  onOpenChange,
  availableTreatments,
  selectedTreatments,
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState<string>("dental");
  const [selectedServices, setSelectedServices] = useState({
    dental: [...selectedTreatments.dental],
    cosmetic: [...selectedTreatments.cosmetic],
  });
  
  const handleToggleTreatment = (treatment: Treatment, category: 'dental' | 'cosmetic') => {
    setSelectedServices(prev => {
      const categoryItems = [...prev[category]];
      const existingIndex = categoryItems.findIndex(t => t.id === treatment.id);
      
      if (existingIndex >= 0) {
        categoryItems.splice(existingIndex, 1);
      } else {
        // Make a copy of the treatment and mark it as enabled
        const newTreatment = { ...treatment, enabled: true };
        categoryItems.push(newTreatment);
      }
      
      return { ...prev, [category]: categoryItems };
    });
  };
  
  const handlePriceChange = (id: string, value: string, category: 'dental' | 'cosmetic') => {
    setSelectedServices(prev => {
      const categoryItems = [...prev[category]];
      const index = categoryItems.findIndex(t => t.id === id);
      
      if (index >= 0) {
        const updatedTreatment = { 
          ...categoryItems[index], 
          customPrice: value === '' ? undefined : parseFloat(value) 
        };
        
        categoryItems[index] = updatedTreatment;
      }
      
      return { ...prev, [category]: categoryItems };
    });
  };
  
  const handleSave = () => {
    onSave(selectedServices);
    onOpenChange(false);
  };
  
  const isSelected = (id: string, category: 'dental' | 'cosmetic') => {
    return selectedServices[category].some(t => t.id === id);
  };
  
  const getServicePrice = (id: string, defaultPrice: number, category: 'dental' | 'cosmetic') => {
    const service = selectedServices[category].find(t => t.id === id);
    return service?.customPrice !== undefined ? service.customPrice : defaultPrice;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Services</DialogTitle>
          <DialogDescription>
            Select services you offer and set your custom pricing.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="dental" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="dental">Dental Services</TabsTrigger>
            <TabsTrigger value="cosmetic">Cosmetic Services</TabsTrigger>
          </TabsList>
          
          <TabsContent value="dental" className="max-h-[400px] overflow-y-auto">
            {availableTreatments.dental.map(treatment => (
              <div key={treatment.id} className="flex items-center justify-between py-3 border-b">
                <div className="flex items-center">
                  <Checkbox 
                    id={`dental-${treatment.id}`}
                    checked={isSelected(treatment.id, 'dental')}
                    onCheckedChange={() => handleToggleTreatment(treatment, 'dental')}
                    className="mr-3"
                  />
                  <div>
                    <label htmlFor={`dental-${treatment.id}`} className="font-medium cursor-pointer">
                      {treatment.name}
                    </label>
                    <p className="text-sm text-muted-foreground">{treatment.description}</p>
                  </div>
                </div>
                
                {isSelected(treatment.id, 'dental') && (
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      value={getServicePrice(treatment.id, treatment.defaultPrice, 'dental')}
                      onChange={(e) => handlePriceChange(treatment.id, e.target.value, 'dental')}
                      className="w-20 text-right"
                    />
                  </div>
                )}
              </div>
            ))}
          </TabsContent>
          
          <TabsContent value="cosmetic" className="max-h-[400px] overflow-y-auto">
            {availableTreatments.cosmetic.map(treatment => (
              <div key={treatment.id} className="flex items-center justify-between py-3 border-b">
                <div className="flex items-center">
                  <Checkbox 
                    id={`cosmetic-${treatment.id}`}
                    checked={isSelected(treatment.id, 'cosmetic')}
                    onCheckedChange={() => handleToggleTreatment(treatment, 'cosmetic')}
                    className="mr-3"
                  />
                  <div>
                    <label htmlFor={`cosmetic-${treatment.id}`} className="font-medium cursor-pointer">
                      {treatment.name}
                    </label>
                    <p className="text-sm text-muted-foreground">{treatment.description}</p>
                  </div>
                </div>
                
                {isSelected(treatment.id, 'cosmetic') && (
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      value={getServicePrice(treatment.id, treatment.defaultPrice, 'cosmetic')}
                      onChange={(e) => handlePriceChange(treatment.id, e.target.value, 'cosmetic')}
                      className="w-20 text-right"
                    />
                  </div>
                )}
              </div>
            ))}
          </TabsContent>
        </Tabs>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave}>Save Services</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddServiceModal;
